import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ZusAggregatedProfile } from '@ctw/shared/content/zus-aggregated-profile';
import { CTWProvider } from '@ctw/shared/context/ctw-context';
import { PatientProvider } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type EHR, ehrSystemURLs } from '@ctw/shared/context/writeback-provider';
import { useTriggerHotloadPatient } from '@ctw/shared/hooks/use-trigger-hotload-patient';
import { getEnvFromCurrentHostname } from '@ctw/shared/utils/environments';
import { useEffect, useMemo } from 'react';
import { SmartAppProvider, useSmartApp } from './context/smart-app-context';

const ZAPV2 = () => {
  const telemetry = useTelemetry();

  useEffect(() => {
    telemetry.logger.info('Mounting smart-on-fhir zap');
    return () => telemetry.logger.info('Unmounting smart-on-fhir zap');
  }, [telemetry]);

  const { ehrPatientId, zusAccessToken, ehr, ehrContext } = useSmartApp();

  const systemURL = useMemo(() => {
    const resolvedSystemURL = ehrSystemURLs[ehr as EHR];
    if (!resolvedSystemURL) {
      throw new Error('EHR not supported');
    }
    return resolvedSystemURL;
  }, [ehr]);

  // Trigger hotloading as early as possible — before CTWProvider mounts and the
  // patient resource resolves. The bff-api endpoint resolves the UPID server-side.
  // No-ops until the token and identifier are present.
  useTriggerHotloadPatient({
    authToken: zusAccessToken,
    patientID: ehrPatientId,
    systemURL,
  });

  // For Athena we want to refresh the chart after saving a resource
  // so that the user can see the effect of their action. Though we do
  // not want to refresh the chart after saving a Basic resource since
  // those are not reflected in Athena.
  const refreshChart = () => {
    if (ehr !== 'athena') {
      return;
    }
    window.parent.postMessage(
      {
        type: 'embeddedAppAPIMessage',
        method: 'notifyPatientDataChange',
        methodVersion: '1.0.0',
      },
      getEnvFromCurrentHostname() === 'prod'
        ? 'https://athenanet.athenahealth.com'
        : 'https://preview.athenahealth.com',
    );
  };

  if (!(ehrPatientId && zusAccessToken && ehr)) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <CTWProvider
      service="smart-on-fhir"
      serviceVersion="v2"
      ehr={ehr}
      authToken={zusAccessToken}
      onResourceSave={(resource) => {
        if (resource.resourceType !== 'Basic') {
          refreshChart();
        }
      }}
      writebacks={{
        onWritebackSuccess: refreshChart,
      }}
      ehrContext={ehrContext}
    >
      <PatientProvider patientID={ehrPatientId} systemURL={systemURL}>
        <ZusAggregatedProfile />
      </PatientProvider>
    </CTWProvider>
  );
};

const ZAPWrapper = () => (
  <SmartAppProvider>
    <ZAPV2 />
  </SmartAppProvider>
);

export default ZAPWrapper;
